/**
 * Typed endpoint helpers, one function per API path.
 *
 * Keeping them in one file is fine while the surface is small; once we have
 * the full codegen output (npm run gen:api) we can move to per-resource modules.
 */

import { apiFetch } from "./api";
import { FORM_N400 } from "./constants";
import type {
  AddressListResponse,
  AddressResponse,
  CaseListResponse,
  CaseResponse,
  CheckoutResponse,
  DocumentListResponse,
  DocumentResponse,
  ChildListResponse,
  ChildResponse,
  CreateAddressRequest,
  CreateCaseRequest,
  CreateChildRequest,
  CreateCriminalEventRequest,
  CreateEmploymentRequest,
  CreateMarriageRequest,
  CreatePersonRequest,
  CreateTravelRequest,
  CriminalEventListResponse,
  CriminalEventResponse,
  EmploymentListResponse,
  EmploymentResponse,
  EvaluationResult,
  FormResponseResponse,
  FormResponsesListResponse,
  GateRequest,
  GateResponse,
  GenerateRequest,
  GenerateResponse,
  JobDownloadResponse,
  JobResponse,
  MarriageListResponse,
  MarriageResponse,
  PersonListResponse,
  PersonResponse,
  TravelListResponse,
  TravelResponse,
  UpdateAddressRequest,
  UpdatePersonRequest,
  UserResponse,
} from "./api-types";

// --- auth -----------------------------------------------------------------

export const auth = {
  /** GET /v1/me — returns the current user or throws 401. */
  me: () => apiFetch<UserResponse>("/v1/me"),

  /** POST /v1/auth/logout */
  logout: () => apiFetch<void>("/v1/auth/logout", { method: "POST" }),

  /**
   * Full URL to start the Google OIDC flow.
   * The Go server sets state cookies and redirects to Google.
   */
  loginUrl: () => "/v1/auth/google/login",

  /**
   * POST /v1/auth/test-login — closed-alpha login by email. Backend
   * looks up a user with a `dev`-provider identity for that email
   * (seeded via `admin-cli add-test-user`). On success, sets the
   * gc_session + gc_csrf cookies, returns {user_id, email}. 404 if
   * the email isn't a registered test user.
   */
  testLogin: (email: string) =>
    apiFetch<{ user_id: string; email: string }>("/v1/auth/test-login", {
      method: "POST",
      json: { email },
    }),
};

// --- cases ----------------------------------------------------------------

export const cases = {
  list: () => apiFetch<CaseListResponse>("/v1/cases"),

  get: (id: string) => apiFetch<CaseResponse>(`/v1/cases/${id}`),

  create: (req: CreateCaseRequest) =>
    apiFetch<CaseResponse>("/v1/cases", { method: "POST", json: req }),

  gate: (id: string, answers: GateRequest) =>
    apiFetch<GateResponse>(`/v1/cases/${id}/gate`, { method: "POST", json: answers }),

  evaluation: (id: string) => apiFetch<EvaluationResult>(`/v1/cases/${id}/evaluation`),

  generate: (id: string, req: GenerateRequest = {}) =>
    apiFetch<GenerateResponse>(`/v1/cases/${id}/generate`, { method: "POST", json: req }),

  /** POST /v1/cases/:id/checkout — creates a Stripe Checkout Session. */
  checkout: (id: string) =>
    apiFetch<CheckoutResponse>(`/v1/cases/${id}/checkout`, { method: "POST" }),

  /**
   * GET /v1/cases/:id/dev-mark-paid — DEV_AUTO_PAY bypass.
   * Only active when the server has DEV_AUTO_PAY=true (or mock mode).
   */
  devMarkPaid: (id: string) =>
    apiFetch<CaseResponse>(`/v1/cases/${id}/dev-mark-paid`),

  // jobs (poll PDF render)
  listJobs: (caseId: string) =>
    apiFetch<{ items: JobResponse[]; total: number }>(`/v1/cases/${caseId}/jobs`),

  getJob: (caseId: string, jobId: string) =>
    apiFetch<JobResponse>(`/v1/cases/${caseId}/jobs/${jobId}`),

  jobDownload: (caseId: string, jobId: string) =>
    apiFetch<JobDownloadResponse>(`/v1/cases/${caseId}/jobs/${jobId}/download`),

  /**
   * GET /v1/cases/:id/packet-status — reports whether the current
   * snapshot would yield a different PDF than the last rendered one.
   * Used by /review to hide the Re-generate button when nothing has
   * changed since the last successful render.
   */
  packetStatus: (caseId: string) =>
    apiFetch<{
      packet_version: number;
      last_packet_version: number;
      is_outdated: boolean;
      has_rendered_packet: boolean;
    }>(`/v1/cases/${caseId}/packet-status`),

  /** POST /v1/cases/:id/send-resume-link — sends a magic resume link to the applicant's email. */
  sendResumeLink: (id: string) =>
    apiFetch<{ status: string; to: string }>(`/v1/cases/${id}/send-resume-link`, { method: "POST" }),
};

// --- persons --------------------------------------------------------------

export const persons = {
  list: (caseId: string) =>
    apiFetch<PersonListResponse>(`/v1/cases/${caseId}/persons`),

  get: (caseId: string, personId: string) =>
    apiFetch<PersonResponse>(`/v1/cases/${caseId}/persons/${personId}`),

  create: (caseId: string, req: CreatePersonRequest) =>
    apiFetch<PersonResponse>(`/v1/cases/${caseId}/persons`, {
      method: "POST",
      json: req,
    }),

  update: (caseId: string, personId: string, req: UpdatePersonRequest) =>
    apiFetch<PersonResponse>(`/v1/cases/${caseId}/persons/${personId}`, {
      method: "PATCH",
      json: req,
    }),
};

// --- form_responses -------------------------------------------------------

export const formResponses = {
  list: (caseId: string) =>
    apiFetch<FormResponsesListResponse>(`/v1/cases/${caseId}/form-responses`),

  upsert: (caseId: string, fieldKey: string, value: unknown) =>
    apiFetch<FormResponseResponse>(
      `/v1/cases/${caseId}/form-responses/${FORM_N400.code}/${FORM_N400.edition}/${fieldKey}`,
      // Backend FormResponseRequest is `{value: <any>}`, not the
      // raw value — see services/api/internal/cases/form_responses.go.
      { method: "PUT", json: { value } },
    ),

  delete: (caseId: string, fieldKey: string) =>
    apiFetch<void>(
      `/v1/cases/${caseId}/form-responses/${FORM_N400.code}/${FORM_N400.edition}/${fieldKey}`,
      { method: "DELETE" },
    ),
};

// --- addresses ------------------------------------------------------------

export const addresses = {
  list: (caseId: string, personId: string) =>
    apiFetch<AddressListResponse>(
      `/v1/cases/${caseId}/persons/${personId}/addresses`,
    ),

  create: (caseId: string, personId: string, req: CreateAddressRequest) =>
    apiFetch<AddressResponse>(
      `/v1/cases/${caseId}/persons/${personId}/addresses`,
      { method: "POST", json: req },
    ),

  update: (
    caseId: string,
    personId: string,
    addressId: string,
    req: UpdateAddressRequest,
  ) =>
    apiFetch<AddressResponse>(
      `/v1/cases/${caseId}/persons/${personId}/addresses/${addressId}`,
      { method: "PUT", json: req },
    ),

  delete: (caseId: string, personId: string, addressId: string) =>
    apiFetch<void>(
      `/v1/cases/${caseId}/persons/${personId}/addresses/${addressId}`,
      { method: "DELETE" },
    ),
};

// --- employments / travels / marriages / children / criminal-events ------
//
// All five share the same nested-CRUD shape: list/create under
// /v1/cases/{cid}/persons/{pid}/{resource}; get/put/delete under .../{id}.
// We generate a typed namespace per resource via this factory.

function nestedCrud<TList, TItem, TCreate>(resource: string) {
  return {
    list: (caseId: string, personId: string) =>
      apiFetch<TList>(`/v1/cases/${caseId}/persons/${personId}/${resource}`),

    create: (caseId: string, personId: string, req: TCreate) =>
      apiFetch<TItem>(`/v1/cases/${caseId}/persons/${personId}/${resource}`, {
        method: "POST",
        json: req,
      }),

    update: (
      caseId: string,
      personId: string,
      itemId: string,
      req: TCreate,
    ) =>
      apiFetch<TItem>(
        `/v1/cases/${caseId}/persons/${personId}/${resource}/${itemId}`,
        { method: "PUT", json: req },
      ),

    delete: (caseId: string, personId: string, itemId: string) =>
      apiFetch<void>(
        `/v1/cases/${caseId}/persons/${personId}/${resource}/${itemId}`,
        { method: "DELETE" },
      ),
  };
}

export const employments = nestedCrud<
  EmploymentListResponse,
  EmploymentResponse,
  CreateEmploymentRequest
>("employments");

export const travels = nestedCrud<
  TravelListResponse,
  TravelResponse,
  CreateTravelRequest
>("travels");

export const marriages = nestedCrud<
  MarriageListResponse,
  MarriageResponse,
  CreateMarriageRequest
>("marriages");

export const children = nestedCrud<
  ChildListResponse,
  ChildResponse,
  CreateChildRequest
>("children");

// --- documents ------------------------------------------------------------

export const documents = {
  list: (caseId: string) =>
    apiFetch<DocumentListResponse>(`/v1/cases/${caseId}/documents`),

  upload: (caseId: string, file: File, documentType?: string) => {
    const form = new FormData();
    form.append("file", file);
    if (documentType) form.append("document_type", documentType);
    return apiFetch<DocumentResponse>(`/v1/cases/${caseId}/documents`, {
      method: "POST",
      body: form,
    });
  },

  delete: (caseId: string, docId: string) =>
    apiFetch<void>(`/v1/cases/${caseId}/documents/${docId}`, { method: "DELETE" }),
};

// --- AI assistant chat ----------------------------------------------------

export type ChatMessageItem = {
  id: string;
  role: "user" | "assistant";
  content: string;
  wizard_step?: string;
  field_key?: string;
  created_at: string;
};

export type ChatHistoryResponse = {
  items: ChatMessageItem[];
  total: number;
  available: boolean;
};

export const chat = {
  history: (caseId: string) =>
    apiFetch<ChatHistoryResponse>(`/v1/cases/${caseId}/chat`),

  clear: (caseId: string) =>
    apiFetch<void>(`/v1/cases/${caseId}/chat`, { method: "DELETE" }),

  /**
   * Opens a streaming SSE connection for the AI reply.
   * Returns a ReadableStreamDefaultReader over text lines.
   * Caller is responsible for calling reader.cancel() when done.
   */
  sendStream: async (
    caseId: string,
    message: string,
    wizardStep?: string,
    fieldKey?: string,
  ): Promise<ReadableStreamDefaultReader<Uint8Array>> => {
    // Read CSRF double-submit cookie — same pattern as apiFetch().
    const csrfCookie = document.cookie.match(/(^| )gc_csrf=([^;]+)/);
    const csrfToken = csrfCookie ? decodeURIComponent(csrfCookie[2]) : null;

    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken;

    // URL uses /v1/... (no /api prefix) — consistent with Vite proxy and apiFetch.
    const res = await fetch(`/v1/cases/${caseId}/chat`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({
        message,
        wizard_step: wizardStep,
        field_key: fieldKey,
      }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    if (!res.body) throw new Error("No response body");
    return res.body.getReader();
  },
};

export const criminalEvents = nestedCrud<
  CriminalEventListResponse,
  CriminalEventResponse,
  CreateCriminalEventRequest
>("criminal-events");
